import { useEffect, useState } from "react";
import { modelLabel } from "../../shared/models";
import { asksForAccess, PROPOSES, proposesText, type AccessPayload, type AgentEditPayload, type ApprovalDTO, type SkillChangePayload, type SkillFileChange } from "../../shared/types";
import { formatApprovalText } from "../lib/approval-text";
import { AgentAvatar } from "./AgentAvatar";
import { formatBytes } from "./Files";
import { LineDiff } from "./LineDiff";
import { Markdown } from "./Markdown";
import { TextLink } from "./TextLink";
import { Badge, Collapsible } from "./ui";
import { Decided, LockedVerdicts, VerdictBar } from "./Verdicts";

/** How each kind reads: on a list ("Goal", "wants to work toward") and on its card ("proposes to add a goal"). */
export const KIND: Record<ApprovalDTO["kind"], { label: string; verb: string; proposes: string }> = {
  goal: { label: "Goal", verb: "wants to work toward", proposes: PROPOSES.goal },
  // History: the task feature went on 2026-10-19; a decided task proposal in an old chat still reads.
  task: { label: "Task (retired)", verb: "wanted to take on", proposes: PROPOSES.task },
  // History: a schedule made of steps, from before 070; a new one is `schedule`.
  workflow: { label: "Schedule (old)", verb: "wanted to run on a schedule", proposes: PROPOSES.workflow },
  schedule: { label: "Schedule", verb: "wants to run on a schedule", proposes: PROPOSES.schedule },
  sub_agent: { label: "Sub-agent", verb: "wants a sub-agent", proposes: PROPOSES.sub_agent },
  question: { label: "Question", verb: "asks", proposes: PROPOSES.question },
  note: { label: "Note", verb: "suggests for your note", proposes: PROPOSES.note },
  skill: { label: "Skill", verb: "wants to add a skill", proposes: PROPOSES.skill },
  review: { label: "Request", verb: "asks for a verdict on", proposes: PROPOSES.review },
  file_share: { label: "File share", verb: "wants to share a file", proposes: PROPOSES.file_share },
  agent_edit: { label: "Instructions", verb: "wants to change its instructions or description", proposes: PROPOSES.agent_edit },
  skill_change: { label: "Skill change", verb: "wants to change a skill", proposes: PROPOSES.skill_change },
  // Offered by a person, not proposed by its agent (AIT-62): its own card says who offers it.
  schedule_handover: { label: "Schedule handover", verb: "offers a schedule on", proposes: PROPOSES.schedule_handover },
  access: { label: "Access", verb: "asks for access", proposes: PROPOSES.access },
  // Decided on its own card, which reads the real call (AIT-108).
  action: { label: "Sensitive action", verb: "needs an approval for", proposes: PROPOSES.action },
};

/** The label on a request's badge: its kind's, except an agent's own edit that changes only its description. */
export function kindLabel(a: ApprovalDTO): string {
  const p = a.payload as AgentEditPayload;
  return a.kind === "agent_edit" && p.description && !p.instructions ? "Description" : KIND[a.kind].label;
}

/** What it wants on a list, after the agent's name: its kind's words, or the agent a Platform Assistant's card changes (AIT-52). */
export function kindVerb(a: ApprovalDTO): string {
  if (a.kind === "access") return asksForAccess(a.payload as AccessPayload);
  return a.kind === "agent_edit" && a.targetAgentName ? `wants to ${proposesText(a)}` : KIND[a.kind].verb;
}

/** One file of a proposed skill version: what happens to it, then its line diff, or its size when it is not text. */
function FileChange({ f }: { f: SkillFileChange }) {
  const word = { added: "new file", removed: "removed", changed: "changed" }[f.change];
  return (
    <li className="min-w-0 space-y-1">
      <p className="text-xs">
        <span className="font-mono text-fg">{f.path}</span> <span className="text-faint">· {word}</span>
        {f.executable && <span className="text-faint"> · {f.executable.after ? "may now be run" : "may no longer be run"}</span>}
      </p>
      {f.binary ? (
        <p className="text-xs text-muted">
          Not text · {f.change === "changed" ? `${formatBytes(f.beforeSize ?? 0)} → ${formatBytes(f.afterSize ?? 0)}` : formatBytes((f.change === "added" ? f.afterSize : f.beforeSize) ?? 0)}
        </p>
      ) : (
        (f.before !== undefined || f.after !== undefined) && (f.before ?? "") !== (f.after ?? "") && <LineDiff before={f.before ?? ""} after={f.after ?? ""} />
      )}
    </li>
  );
}

/**
 * What an edit would write — an agent's own, or the Platform Assistant's for another agent: each part as a line diff
 * against what it says now, the whole new text a click away, and a restore says which version it brings back.
 */
function agentEditRows(p: AgentEditPayload): [string, React.ReactNode][] {
  const rows: [string, React.ReactNode][] = [];
  if (p.instructions?.restoredFrom) rows.push(["Brings back", `Version ${p.instructions.restoredFrom}'s instructions, as the next version; the model stays as it is`]);
  if (p.instructions)
    rows.push([
      "Instructions",
      <div key="instructions" className="min-w-0 space-y-2">
        <LineDiff before={p.instructions.before} after={p.instructions.after} />
        <Collapsible summary={<span className="text-xs text-muted">The full new text{p.instructions.baseVersion ? `, to follow version ${p.instructions.baseVersion}` : ""}</span>}>
          <pre className="scroll-thin max-h-96 overflow-auto rounded-xl bg-surface p-3 text-xs whitespace-pre-wrap ring-1 ring-line">{p.instructions.after}</pre>
        </Collapsible>
      </div>,
    ]);
  if (p.description) rows.push(["Description", <LineDiff key="description" before={p.description.before} after={p.description.after} />]);
  return rows;
}

/** What a skill change would write: the skill, who has it now, and the change — every file of a new version, or the agents. */
function skillChangeRows(a: ApprovalDTO, p: SkillChangePayload): [string, React.ReactNode][] {
  const rows: [string, React.ReactNode][] = [];
  rows.push(["Skill", `${p.name} · ${p.level === "person" ? `${a.ownerName ?? "one person"}'s own skill` : "an agent skill"}${p.base.version ? ` · version ${p.base.version}` : ""}`]);
  if (p.usedBy?.length) rows.push(["Used by", p.usedBy.join(", ")]);
  if (p.action === "update") {
    if (p.description) rows.push(["Description", <LineDiff key="description" before={p.description.before} after={p.description.after} />]);
    if (p.changes?.length)
      rows.push([
        "Files",
        <ul key="files" className="min-w-0 space-y-3">
          {p.changes.map((f) => <FileChange key={f.path} f={f} />)}
        </ul>,
      ]);
  } else if (p.action === "archive") {
    rows.push(["Effect", "Archived: offered to no agent and no person any more. Nothing is deleted, and it can be brought back on the Skills page."]);
  } else if (p.action === "share") {
    if (p.gains?.length) rows.push(["Gains it", p.gains.join(", ")]);
  } else if (p.loses?.length) {
    rows.push(["Loses it", p.loses.join(", ")]);
  }
  return rows;
}

/** What an access request would give: who, what, and everything else Approve grants with it, so nothing is a surprise. */
function accessRows(p: AccessPayload): [string, React.ReactNode][] {
  const rows: [string, React.ReactNode][] = [];
  if (p.askedBy) rows.push(["Asked by", `${p.askedBy.name}, through the Platform Assistant`]);
  if (p.person) rows.push(["For", p.person.name]);
  if (p.agent) rows.push([p.person ? "Agent" : "For", p.agent.name]);
  if (p.connector) rows.push(["Connector", `${p.connector.name}${p.connector.ownLogin && p.person ? " · they connect their own account" : ""}`]);
  if (p.level) rows.push(["Level", `${p.from ? `${p.from.name} → ` : ""}${p.level.name}`]);
  if (p.missing?.length)
    rows.push([
      "Also gives them",
      <ul key="missing" className="space-y-0.5">
        {p.missing.map((m) => (
          <li key={m.id}>
            {m.name}, at its default level{m.ownLogin ? " · they connect their own account" : ""}
          </li>
        ))}
      </ul>,
    ]);
  if (p.alreadyHeld) rows.push(["Outcome", "Already held, so nothing was written"]);
  return rows;
}

/** Who a file would be shared with, in words: everyone, the agent's scope, or the people and agents it names. */
const shareWith = (w: unknown) =>
  w === "everyone" ? "Everyone" : w === "scope" ? "Everyone working under its scope" : Array.isArray(w) ? w.map(String).join(", ") : "";

/** One proposed file as it rides in the payload: its bytes, and its text when it is UTF-8 (null when it is not). */
function decodeFile(base64: string | undefined): { bytes: number; text: string | null } {
  try {
    const raw = atob(base64 ?? "");
    const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
    try {
      return { bytes: bytes.length, text: new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes) };
    } catch {
      return { bytes: bytes.length, text: null };
    }
  } catch {
    return { bytes: 0, text: null };
  }
}

const SKILL_MAIN = "SKILL.md";

/**
 * What was proposed, in rows: the same table on the card in the chat and on the Requests page. Every row is
 * conditional, so a reader whose copy carries no payload gets no empty box.
 */
export function ProposalDetails({ a, canDecide = false }: { a: ApprovalDTO; canDecide?: boolean }) {
  const p = a.payload as Record<string, any>;
  const rows: [string, React.ReactNode][] = [];
  if (a.kind === "goal") {
    if (p.target) rows.push(["Achieved when", p.target]);
    if (p.description) rows.push(["Details", p.description]);
    // An agent has one goal (093): approving this one replaces the goal it has, and the card says which.
    if (p.replaces) rows.push([a.status === "approved" ? "Replaced" : "Replaces", p.replaces.title]);
  } else if (a.kind === "task") {
    if (p.description) rows.push(["Details", p.description]);
    if (p.priority && p.priority !== "normal") rows.push(["Priority", p.priority]);
    if (p.dueAt) rows.push(["Due", new Date(p.dueAt).toLocaleDateString()]);
  } else if (a.kind === "workflow") {
    const t = p.trigger;
    if (t) rows.push(["When", t.type === "schedule" ? `${t.cron}${t.timezone ? ` (${t.timezone})` : ""}` : t.type ?? "manual"]);
    if (p.steps?.length)
      rows.push([
        "Steps",
        <ol key="steps" className="list-decimal space-y-0.5 pl-4">
          {p.steps.map((s: any, i: number) => (
            <li key={i} className="truncate">
              {s.type === "agent" ? `${s.agent}: ${String(s.prompt ?? "").slice(0, 140)}` : s.type === "notify" ? `notify${s.slack ? ` (Slack ${s.slack})` : ""}` : s.type}
            </li>
          ))}
        </ol>,
      ]);
  } else if (a.kind === "schedule") {
    rows.push(["When", p.trigger === "cron" ? `${p.cron}${p.timezone ? ` (${p.timezone})` : ""}` : p.trigger === "once" && p.runAt ? `once at ${new Date(p.runAt).toLocaleString()}` : "by hand"]);
    if (p.prompt) rows.push(["What to do", <span key="prompt" className="whitespace-pre-wrap">{String(p.prompt)}</span>]);
    if (p.model) rows.push(["Model", modelLabel(String(p.model))]);
    if (p.slackDm) rows.push(["Delivery", "also to the owner's Slack DM"]);
    if (p.requestedFor?.name) rows.push(["Requested for", p.requestedFor.name]);
    // Approving makes it the approver's (D6): said to whoever may approve it, before they do, and on the record after.
    if (a.status === "approved" && a.decidedByName) rows.push(["Whose", `${a.decidedByName}'s: it runs as them and answers in their chat`]);
    else if (a.status === "pending" && canDecide)
      rows.push(["Whose", `Approving makes it yours: it runs as you, with your access, and answers in your own chat${p.requestedFor?.name ? `, not ${p.requestedFor.name}'s` : ""}`]);
  } else if (a.kind === "question") {
    if (p.description) rows.push(["Details", p.description]);
  } else if (a.kind === "agent_edit") {
    rows.push(...agentEditRows(p as AgentEditPayload));
  } else if (a.kind === "skill_change") {
    if (p.action) rows.push(...skillChangeRows(a, p as SkillChangePayload));
  } else if (a.kind === "access") {
    rows.push(...accessRows(p as AccessPayload));
  } else if (a.kind === "schedule_handover") {
    // Its own card draws what was offered (AIT-62).
  } else if (a.kind === "file_share") {
    if (p.fileName) rows.push(["File", p.fileName]);
    if (shareWith(p.with)) rows.push(["Shared with", shareWith(p.with)]);
  } else if (a.kind === "skill") {
    // A skill is a folder: its one line, SKILL.md whole — what every later run will follow, read before the yes (D8) —
    // then the rest of the folder with sizes. Read-only, drawn as About me → My skills draws a written one.
    if (p.description) rows.push(["Description", p.description]);
    const files = (p.files ?? []) as { path: string; base64?: string; executable?: boolean }[];
    const main = files.find((f) => f.path === SKILL_MAIN);
    if (main)
      rows.push([
        SKILL_MAIN,
        <pre key="main" className="scroll-thin max-h-80 overflow-auto rounded-xl bg-surface p-3 text-xs whitespace-pre-wrap">
          {decodeFile(main.base64).text ?? "(not text)"}
        </pre>,
      ]);
    const others = files.filter((f) => f.path !== SKILL_MAIN);
    if (others.length)
      rows.push([
        "Other files",
        <ul key="files" className="space-y-0.5">
          {others.map((f) => (
            <li key={f.path} className="truncate font-mono text-xs">
              {f.path} · {formatBytes(decodeFile(f.base64).bytes)}
              {f.executable ? " · executable" : ""}
            </li>
          ))}
        </ul>,
      ]);
  } else {
    if (p.slug) rows.push(["Slug", <span key="slug" className="font-mono">{p.slug}</span>]);
    if (p.scope) rows.push(["Focus", p.scope]);
    if (p.connectorSlugs?.length) rows.push(["Connectors", p.connectorSlugs.join(", ")]);
    if (p.systemPrompt) rows.push(["Instructions", <span key="instructions" className="line-clamp-4 whitespace-pre-wrap">{p.systemPrompt}</span>]);
  }
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-xl bg-fill-subtle p-3 text-sm sm:grid-cols-[7rem_minmax(0,1fr)]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-xs font-medium text-muted sm:pt-0.5">{k}</dt>
          <dd className="min-w-0 break-words text-muted">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Where what was approved now lives. Every one of them is an admin page, so a member gets the words without a link. */
function resultLink(a: ApprovalDTO): { to: string; label: string } | null {
  if (!a.resultId) return null;
  // What it opened is settings, not a new thing to go and look at.
  if (a.kind === "access") return null;
  if (a.kind === "sub_agent") return { to: `/admin/agents/${a.resultId}`, label: "open the sub-agent" };
  // The task it made is gone with the task feature (2026-10-19): the words stay, the link does not.
  if (a.kind === "task") return null;
  if (a.kind === "skill" || a.kind === "skill_change") return { to: `/admin/skills/${a.resultId}`, label: "open the skill" };
  if (a.kind === "agent_edit") return { to: `/admin/agents/${a.targetAgentId ?? a.agentId}`, label: "open the agent" };
  if (a.kind === "file_share") return { to: "/admin/files", label: "open the files" };
  if (a.kind === "schedule_handover") return { to: `/admin/agents/${a.agentId}?tab=schedules`, label: "open the schedule" };
  const tab = a.kind === "goal" ? "goals" : "schedules";
  return { to: `/admin/agents/${a.agentId}?tab=${tab}`, label: `open the ${KIND[a.kind].label.toLowerCase()}` };
}

/** What happened, once somebody decided: the verdict, and a link to what it created. An older answer keeps its own label. */
function ProposalDecided({ a, admin }: { a: ApprovalDTO; admin: boolean }) {
  const legacy = a.options.find((o) => o.key === a.decisionKey)?.label;
  const link = a.status === "approved" ? resultLink(a) : null;
  return (
    <Decided a={a} word={legacy}>
      {link && admin && (
        <>
          <span className="text-faint">·</span>
          <TextLink quiet to={link.to}>
            {link.label}
          </TextLink>
        </>
      )}
    </Decided>
  );
}

/**
 * One proposal in a transcript: what the agent wants to start on its own, its details and its reason, with the three
 * verdicts for the people who answer for it. Everyone else reads it, the verdicts drawn off, and sees who decides.
 * The Requests page shows the same proposal as a full document and shares this file's details.
 */
export function ProposalCard({ approval, canDecide, admin = false }: { approval: ApprovalDTO; canDecide: boolean; admin?: boolean }) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;

  return (
    <div className="card border-l-2 border-l-haze-deep p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="blue">{kindLabel(a)}</Badge>
        <span className="text-muted">
          <span className="font-medium text-fg">{a.agentName}</span> {a.kind === "access" ? asksForAccess(a.payload as AccessPayload) : `proposes to ${proposesText(a)}`}
        </span>
      </div>
      <p className="mt-2 text-reading leading-6 font-medium tracking-tight break-words">{a.title}</p>
      <ProposalDetails a={a} canDecide={canDecide} />
      {a.reason && (
        <div className="mt-3">
          <Collapsible summary={<span className="text-xs text-muted">Why</span>}>
            <Markdown className="text-sm leading-6 [&_h4]:mt-5 [&_h4]:text-xs [&_h4]:tracking-wider [&_h4]:text-muted">{formatApprovalText(a.reason)}</Markdown>
          </Collapsible>
        </div>
      )}
      {a.status !== "pending" ? (
        <ProposalDecided a={a} admin={admin} />
      ) : canDecide ? (
        <div className="mt-4">
          {/* An approval that could not be carried out leaves the proposal waiting, with the reason. */}
          {a.error && <p className="mb-2 text-sm text-danger">Could not apply: {a.error}</p>}
          <VerdictBar a={a} onDecided={(updated) => updated && setLocal(updated)} />
        </div>
      ) : (
        <div className="mt-4">
          <LockedVerdicts a={a} />
        </div>
      )}
    </div>
  );
}
